import React, { useCallback, useEffect, useState } from 'react';
import { askOrbitAction } from '../../components/AskOrbit';
import { Check, Circle, FileText, Lock, Sparkles } from 'lucide-react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { usePermissions } from '../../hooks/usePermissions';
import type { DeliveryAudience, DeliveryControl, DeliveryProject, DeliveryProjectPanel, DeliveryQuote, ProjectDocuments } from '../../types/aai';
import { Action, Card, Chip, ErrorNote, FactList, Kicker, Loading, RailCard, RecordModal, RecordPage, RouteMark, StatusChip, useRecordBack } from '../../components/aa';
import { useParams } from 'react-router-dom';
import { relativeTime } from '../partner/format';
import { DeliveryCard } from './DeliveryCard';
import { RequestRoster } from '../aai/RequestRoster';
import { useOpenWorkspace } from '../../hooks/useOpenWorkspace';

/*
 * The Owner's Project record: the approved business record and its links.
 *
 * Almost everything is derived from the request row and the client's own
 * directory, the same read the Solution Partner gets. What the Owner adds is the
 * one commercial decision that is theirs: accepting a quote. That needs billing
 * authority (`manage_client_billing`), not the program-management grant, so the
 * partner can prepare a quote and never accept it on the client's behalf.
 *
 * Delivery detail lives in Workspace and is read there by its named members.
 * Carrying the work on is a different question from reading that detail, so the
 * Workspace card below offers the one door the lifecycle is actually at, and
 * says plainly when there is no door yet.
 */

const money = (amount: number | null, currency: string): string => {
  if (amount === null) return 'Not priced';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
};

const QUOTE_LABEL: Record<string, string> = {
  accepted: 'Accepted',
  draft: 'Being prepared',
  sent: 'Awaiting your acceptance',
  superseded: 'Superseded',
  declined: 'Declined',
};

const QUOTE_STATUS: Record<string, string> = {
  accepted: 'active',
  draft: 'record_only',
  sent: 'pending',
  superseded: 'retired',
  declined: 'declined',
};

const audienceValue = (audience: DeliveryAudience): string => {
  if (audience.suppressed) return `${audience.scope} · size withheld to protect privacy`;
  if (audience.people === null) return audience.scope;
  return `${audience.scope} · ${audience.people} people`;
};

const ControlRow: React.FC<{ control: DeliveryControl }> = ({ control }) => (
  <div className="flex items-start gap-3 rounded-card bg-canvas-inset px-4 py-3">
    {control.value && <Check size={15} strokeWidth={3} className="mt-0.5 shrink-0 text-[#3F7A2F]" />}
    {!control.value && <Circle size={14} className="mt-0.5 shrink-0 text-ink-soft" />}
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{control.label}</div>
      <div className="mt-0.5 text-[13px] text-ink">
        {control.value}
        {!control.value && <span className="text-ink-soft">{control.unset}</span>}
      </div>
    </div>
  </div>
);

/*
 * The two doors into the Workspace, and an honest state when neither is open.
 *
 * This was a permanently locked button reading "Workspace membership is granted
 * per Project and is not part of any role". That is true of the delivery detail
 * INSIDE the Workspace and false of the thing people come to this panel to do:
 * carry on the scoping conversation, which the server grants to the person who
 * asked, anyone named on the work, the department head, a champion and the
 * Owner. Drawing the lock anyway told the Owner they could not open their own
 * company's scoping, and there was no other way to reach it from here.
 *
 * Which door shows is the lifecycle and never both: scoping runs until the
 * brief becomes a build, and the Planner takes over from there. The employee's
 * panel asks the same question and answers it the same way.
 */
const WorkspaceCard: React.FC<{ project: DeliveryProject }> = ({ project }) => {
  const { opening, open } = useOpenWorkspace();
  const buildId = project.builder_project_id;
  // A Quick Win is built against the request by its named expert, so it has no
  // scoping conversation to carry on. Older ones still carry a problem card
  // from before that rule, so the route is read rather than only the id.
  const scoping = !!project.problem_id && !buildId && project.route !== 'quick_win';
  const planner = () => open(project.client_id, `/customer/agent/${buildId}`);
  const carryOnScoping = () => open(
    project.client_id,
    async () => (await aaiApi.scopeInWorkspace(project.id)).data.path,
  );
  let note = 'Nothing has opened in the Workspace for this project yet. A door appears here once scoping starts, and becomes the Planner once a build does.';
  if (buildId) note = 'The build for this project runs in the Workspace. Continue in the Planner opens it on this project. Delivery detail inside it is still read by its named members.';
  if (scoping) note = 'This is still being scoped, and scoping happens in the Workspace. Continue scoping opens the conversation for this exact request and picks up where it stopped.';
  return (
    <Card pad="md">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="text-[13px] font-semibold text-ink">Workspace</span>
          {!buildId && !scoping && <Chip tone="private" dot>Named delivery members</Chip>}
        </div>
        {buildId && (
          <Action size="sm" variant="quiet" onClick={planner} disabled={opening}>Continue in the Planner</Action>
        )}
        {scoping && (
          <Action size="sm" variant="quiet" onClick={carryOnScoping} disabled={opening}>Continue scoping</Action>
        )}
        {!buildId && !scoping && (
          <Action size="sm" variant="quiet" icon={<Lock size={14} />} disabled reason="Nothing is being scoped or built for this project yet">
            Open Workspace
          </Action>
        )}
      </div>
      <p className="m-0 mt-2 text-[12.5px] leading-[20px] text-ink-dim">{note}</p>
    </Card>
  );
};

const acceptReason = (quote: DeliveryQuote | null, canAccept: boolean): string => {
  if (!quote) return 'Nothing has been quoted yet';
  if (!canAccept) return 'Accepting a quote needs billing authority for this company';
  if (quote.state === 'accepted') return 'This version is already the accepted baseline';
  if (quote.state === 'draft') return 'The Solution Partner has not sent this version yet';
  return 'This version is no longer open for acceptance';
};

const OwnerQuoteCard: React.FC<{
  requestId: string;
  quote: DeliveryQuote | null;
  quotes: DeliveryQuote[];
  onChanged: () => Promise<void>;
}> = ({ requestId, quote: baseline, quotes, onChanged }) => {
  const { can } = usePermissions();
  const [busy, setBusy] = useState(false);
  const canAccept = can('manage_client_billing');
  // A version the partner has sent is the Owner's decision to make, so it is
  // shown ahead of the accepted baseline it would replace.
  const sent = quotes.find((q) => q.state === 'sent') || null;
  const quote = sent || baseline;
  const replaces = sent && baseline && baseline.state === 'accepted' ? baseline : null;
  const acceptable = Boolean(quote && quote.state === 'sent' && canAccept);

  const accept = async () => {
    if (!quote) return;
    setBusy(true);
    try {
      await aaiApi.acceptDeliveryQuote(requestId, quote.id);
      await onChanged();
      toast.success('Accepted. This version is now the approved cost.');
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      toast.error(detail || 'Could not accept this quote.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card pad="md">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="text-[13px] font-semibold text-ink">Quote</span>
          {quote && <StatusChip status={QUOTE_STATUS[quote.state] || quote.state} label={QUOTE_LABEL[quote.state] || quote.state} />}
          {!quote && <Chip>Not quoted</Chip>}
        </div>
        {quote && <span className="font-sans text-[12px] tabular-nums text-ink-soft">v{quote.version} of {quotes.length}</span>}
      </div>
      {!quote && (
        <p className="m-0 mt-2 text-[12.5px] leading-[20px] text-ink-dim">
          The Solution Partner prepares a quote once scoping settles. Cost reads as not quoted until then.
        </p>
      )}
      {quote && (
        <>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            <div className="rounded-lg bg-white px-3.5 py-2.5">
              <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Cost</div>
              <div className="mt-0.5 font-sans text-[15px] font-semibold tabular-nums text-ink">{money(quote.price_amount, quote.price_currency)}</div>
            </div>
            <div className="rounded-lg bg-white px-3.5 py-2.5">
              <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Delivery window</div>
              <div className="mt-0.5 text-[13px] font-semibold text-ink">{quote.delivery_window || 'Not recorded'}</div>
            </div>
            <div className="rounded-lg bg-white px-3.5 py-2.5">
              <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Expected value</div>
              <div className="mt-0.5 text-[13px] font-semibold text-ink">{quote.expected_value || 'Not recorded'}</div>
            </div>
          </div>
          {quote.milestones.length > 0 && (
            <div className="mt-3">
              <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Milestones</div>
              <ul className="m-0 mt-1 list-none p-0">
                {quote.milestones.map((m) => (
                  <li key={m} className="flex gap-2 text-[13px] leading-[21.5px] text-ink-muted">
                    <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-ink-soft" />{m}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <span className="text-[12.5px] leading-[20px] text-ink-dim">
              Accepting sets the approved cost. It does not release anything or open the Workspace.
              {replaces && ` It replaces v${replaces.version}, accepted at ${money(replaces.price_amount, replaces.price_currency)}.`}
            </span>
            <Action onClick={accept} disabled={!acceptable || busy} reason={acceptReason(quote, canAccept)}>
              Accept this quote
            </Action>
          </div>
        </>
      )}
    </Card>
  );
};

type DecisionMode = 'read' | 'needs_info' | 'declined';

const fileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/*
 * The project's room.
 *
 * The call asked that a document uploaded inside a Project stays with that
 * Project, and that anything generated while doing the work stays attached to
 * it too. Both are true in the builder, which is where a project's bytes live;
 * this reads them rather than keeping a rival copy on this side, so the two
 * cannot drift.
 *
 * Uploaded and generated are drawn apart because they answer different
 * questions: one is what people gave the work, the other is what the work gave
 * back. An empty room says why it is empty, since "no documents" and "no build
 * yet" are not the same thing to act on.
 */
const DocumentsSection: React.FC<{ requestId: string }> = ({ requestId }) => {
  const [docs, setDocs] = useState<ProjectDocuments | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setDocs(null);
    setFailed(false);
    aaiApi.getProjectDocuments(requestId)
      .then((res) => { if (live) setDocs(res.data); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [requestId]);

  const total = (docs?.uploaded.length || 0) + (docs?.generated.length || 0);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Documents</div>
        {docs && total > 0 && (
          <span className="text-[12px] tabular-nums text-ink-soft">{total} in this project</span>
        )}
      </div>

      {!docs && !failed && <div className="text-[13px] text-ink-soft">Reading the project room…</div>}
      {failed && (
        <div className="text-[13px] text-ink-dim">
          The Workspace did not answer, so this project's documents could not be listed. The
          documents themselves are unaffected.
        </div>
      )}

      {docs && total === 0 && (
        <p className="m-0 text-[13px] leading-[21px] text-ink-dim">
          {docs.reason || 'No documents have been added to this project yet.'}
        </p>
      )}

      {docs && docs.uploaded.length > 0 && (
        <div className="mb-3">
          <div className="mb-1.5 text-[12px] font-semibold text-ink">Added to the project</div>
          <div className="divide-y divide-line-soft">
            {docs.uploaded.map((f) => (
              <div key={f.id} className="flex items-center gap-2.5 py-2">
                <FileText size={15} strokeWidth={1.5} className="shrink-0 text-ink-soft" />
                <span className="min-w-0 flex-1 truncate text-[13px] text-ink" title={f.path}>{f.name}</span>
                {!f.available && <Chip tone="private">Stored copy missing</Chip>}
                <span className="shrink-0 text-[12px] tabular-nums text-ink-soft">{fileSize(f.size)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {docs && docs.generated.length > 0 && (
        <div>
          <div className="mb-1.5 text-[12px] font-semibold text-ink">Produced by the work</div>
          <div className="divide-y divide-line-soft">
            {docs.generated.map((a) => (
              <div key={a.id} className="flex items-center gap-2.5 py-2">
                <Sparkles size={15} strokeWidth={1.5} className="shrink-0 text-ink-soft" />
                <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{a.title}</span>
                <span className="shrink-0 text-[12px] text-ink-soft">{a.kind.toLowerCase()} · v{a.version}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {docs && docs.screenshots > 0 && (
        <p className="m-0 mt-2 text-[12.5px] text-ink-soft">
          {docs.screenshots} screenshot{docs.screenshots === 1 ? '' : 's'} were also captured during the build.
        </p>
      )}
    </div>
  );
};

const DECISION_FIELD = 'w-full rounded-card border border-line-strong bg-white px-4 py-3 text-[13.5px] leading-[21px] text-ink outline-none placeholder:text-ink-soft focus:border-ink';

const GATE_ONE = new Set(['pending', 'needs_info']);

const headOf = (panel: DeliveryProjectPanel): string | null => {
  const head = panel.people.find((p) => p.role === 'Department head');
  return head?.email ? head.email.toLowerCase() : null;
};

/*
 * The department head's gate on a request. Gate 1 is the recorded lead's and
 * only theirs: a head who holds the role without being the lead reads the same
 * card with the buttons withheld and the reason stated. Clearing sends the
 * request to the company for final approval; it does not make it a Project.
 */
const HeadDecisionCard: React.FC<{
  panel: DeliveryProjectPanel;
  onDecided: () => Promise<void>;
  onClose: () => void;
}> = ({ panel, onDecided, onClose }) => {
  const { can } = usePermissions();
  const [mode, setMode] = useState<DecisionMode>('read');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const project = panel.project;
  const me = (localStorage.getItem('user_email') || '').toLowerCase();
  const lead = headOf(panel);
  const department = project.department || 'department';

  if (!GATE_ONE.has(project.status) || !can('aai_view_department_rollup') || can('aai_approve_requests')) return null;

  if (lead !== me) {
    return (
      <Card pad="md">
        <div className="flex items-center gap-2.5">
          <span className="text-[13px] font-semibold text-ink">Your decision</span>
          <Chip>Recorded lead decides</Chip>
        </div>
        <p className="m-0 mt-2 text-[12.5px] leading-[20px] text-ink-dim">
          {lead ? `${lead} is the recorded ${department} lead, so the first approval is theirs. You can read the request; an admin can make you the lead under Settings.` : `No lead is recorded for ${department}, so nothing can be cleared. Ask an admin to set one.`}
        </p>
      </Card>
    );
  }

  const decide = async (decision: 'approved' | 'needs_info' | 'declined', done: string) => {
    if (decision !== 'approved' && !note.trim()) {
      toast.error('Say why, in a sentence. The requester reads it.');
      return;
    }
    setBusy(true);
    try {
      await aaiApi.decideRequest(project.id, { decision, comment: note.trim() || undefined });
      toast.success(done);
      window.dispatchEvent(new Event('aai:approvals-changed'));
      await onDecided();
      onClose();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      toast.error(detail || 'That decision did not save.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card pad="md">
      <div className="flex items-center gap-2.5">
        <span className="text-[13px] font-semibold text-ink">Your decision</span>
        <Chip tone="warn" dot>{project.status === 'needs_info' ? 'Detail requested' : 'First of two approvals'}</Chip>
      </div>
      <p className="m-0 mt-2 text-[12.5px] leading-[20px] text-ink-dim">
        Clearing sends this to the company for final approval and records your decision on the request. It is not a Project until the Owner says yes.
      </p>
      {mode === 'read' && (
        <div className="mt-4 flex flex-wrap gap-3">
          <Action onClick={() => decide('approved', 'Cleared. The company decides next; the request is not a Project yet.')} disabled={busy} reason="Saving">Clear for company approval</Action>
          <Action variant="quiet" onClick={() => setMode('needs_info')}>Ask for more detail</Action>
          <Action variant="quiet" onClick={() => setMode('declined')}>Decline with a note</Action>
        </div>
      )}
      {mode !== 'read' && (
        <div className="mt-4">
          <div className="text-[13px] font-semibold text-ink">{mode === 'declined' ? 'Why you are declining' : 'What detail you need'}</div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder={mode === 'declined' ? 'One sentence the requester can act on.' : 'How many hours a month does this take today, and for whom?'}
            className={`${DECISION_FIELD} mt-2`}
          />
          <div className="mt-3 flex flex-wrap gap-3">
            <Action variant="ghost" onClick={() => { setMode('read'); setNote(''); }}>Back</Action>
            {mode === 'declined' && <Action onClick={() => decide('declined', 'Declined, with your note on the record.')} disabled={busy} reason="Saving">Decline</Action>}
            {mode === 'needs_info' && <Action onClick={() => decide('needs_info', 'Sent back to the requester with your question.')} disabled={busy} reason="Saving">Send back</Action>}
          </div>
        </div>
      )}
    </Card>
  );
};

/*
 * The Owner's gate on a request. Only a request its department head has cleared
 * (dept_approved) is the Owner's to decide; a pending one is still the head's,
 * and the card says so instead of offering a button the server would refuse.
 */
const OwnerDecisionCard: React.FC<{
  project: DeliveryProject;
  onDecided: () => Promise<void>;
  onClose: () => void;
}> = ({ project, onDecided, onClose }) => {
  const { can } = usePermissions();
  const [mode, setMode] = useState<DecisionMode>('read');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const department = project.department || 'department';

  if (!can('aai_approve_requests')) return null;
  // The server says whose gate it is: a department with no head has nobody to
  // go first, so the Owner decides it in one step.
  let headGoesFirst = project.status === 'pending' && Boolean(project.department);
  if (project.reviewer_gate) headGoesFirst = project.status === 'pending' && project.reviewer_gate === 'department';
  if (headGoesFirst) {
    return (
      <Card pad="md">
        <div className="flex items-center gap-2.5">
          <span className="text-[13px] font-semibold text-ink">Your decision</span>
          <Chip>Waiting on the {department} head</Chip>
        </div>
        <p className="m-0 mt-2 text-[12.5px] leading-[20px] text-ink-dim">
          The {department} head clears a request first. It reaches you once they have, and appears under Decisions needing you on Home.
        </p>
      </Card>
    );
  }
  // A request with no department has no head to go first, so the Owner decides
  // in one step and may still ask for more detail. After the head's clearance
  // the server only accepts approve or decline.
  const oneStep = project.status === 'pending';
  if (!oneStep && project.status !== 'dept_approved') return null;
  let gateChip = <Chip tone="warn" dot>Cleared by the {department} head</Chip>;
  let gateNote = 'Approving makes this a Project and Orbit routes the work from here. Your yes is the second of two.';
  if (oneStep) {
    gateChip = <Chip tone="warn" dot>Yours to decide</Chip>;
    gateNote = 'No department head is recorded for this request, so your decision is the only approval it needs.';
  }

  const decide = async (decision: 'approved' | 'needs_info' | 'declined', done: string) => {
    if (decision !== 'approved' && !note.trim()) {
      toast.error('Say why, in a sentence. The requester reads it.');
      return;
    }
    setBusy(true);
    try {
      await aaiApi.decideRequest(project.id, { decision, comment: note.trim() || undefined });
      toast.success(done);
      window.dispatchEvent(new Event('aai:approvals-changed'));
      await onDecided();
      onClose();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      toast.error(detail || 'That decision did not save.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card pad="md">
      <div className="flex items-center gap-2.5">
        <span className="text-[13px] font-semibold text-ink">Your decision</span>
        {gateChip}
      </div>
      <p className="m-0 mt-2 text-[12.5px] leading-[20px] text-ink-dim">{gateNote}</p>
      {mode === 'read' && (
        <div className="mt-4 flex flex-wrap gap-3">
          <Action onClick={() => decide('approved', 'Approved. Orbit routes the work from here.')} disabled={busy} reason="Saving">Approve</Action>
          {oneStep && <Action variant="quiet" onClick={() => setMode('needs_info')}>Ask for more detail</Action>}
          <Action variant="quiet" onClick={() => setMode('declined')}>Decline with a note</Action>
        </div>
      )}
      {mode !== 'read' && (
        <div className="mt-4">
          <div className="text-[13px] font-semibold text-ink">{mode === 'declined' ? 'Why you are declining' : 'What detail you need'}</div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder={mode === 'declined' ? 'One sentence the requester can act on.' : 'How many hours a month does this take today, and for whom?'}
            className={`${DECISION_FIELD} mt-2`}
          />
          <div className="mt-3 flex flex-wrap gap-3">
            <Action variant="ghost" onClick={() => { setMode('read'); setNote(''); }}>Back</Action>
            {mode === 'declined' && <Action onClick={() => decide('declined', 'Declined, with your note on the record.')} disabled={busy} reason="Saving">Decline</Action>}
            {mode === 'needs_info' && <Action onClick={() => decide('needs_info', 'Sent back to the requester with your question.')} disabled={busy} reason="Saving">Send back</Action>}
          </div>
        </div>
      )}
    </Card>
  );
};

export const ProjectRecordDrawer: React.FC<{
  requestId: string;
  onClose: () => void;
  onChanged?: () => void;
  /** Render as its own page with this back link, instead of a dialog. */
  page?: { backLabel: string };
}> = ({ requestId, onClose, onChanged, page }) => {
  const { can } = usePermissions();
  // Staffing reads the same way the server reads it: the company gate names a
  // builder, and the department head can name a scoper as well, because a
  // request short on detail goes to the head to be filled in.
  const canApprove = can('aai_approve_requests');
  const canDeptRollup = can('aai_view_department_rollup');
  const [data, setData] = useState<DeliveryProjectPanel | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await aaiApi.getDeliveryProject(requestId);
    setData(res.data);
  }, [requestId]);

  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    aaiApi.getDeliveryProject(requestId)
      .then((res) => { if (live) setData(res.data); })
      .catch(() => { if (live) setError('Could not open this Project.'); });
    return () => { live = false; };
  }, [requestId]);

  const reload = async () => {
    await load();
    if (onChanged) onChanged();
  };

  // A decision on the page leaves the reader on the record, which now shows
  // the new state; in the dialog it closes, as before.
  const afterDecision = page ? () => undefined : onClose;

  const project = data?.project;
  const visibleNotes = (data?.notes || []).filter((n) => n.kind === 'client_visible');
  let chipBadge: React.ReactNode;
  if (project?.status_chip) chipBadge = <StatusChip status={project.status_chip} />;
  if (project && !project.status_chip) chipBadge = <StatusChip status={project.stage_chip} label={project.stage_chip} />;

  const title = project?.title || 'Project';
  const sub = project?.detail || 'The approved business record. Delivery detail lives in Workspace.';
  const status = (
    <>
      {error && <ErrorNote message={error} />}
      {!error && !data && <Loading label="Opening the Project" />}
    </>
  );

  if (!data || !project) {
    if (page) {
      return (
        <RecordPage backLabel={page.backLabel} onBack={onClose} kicker={<Kicker>Project</Kicker>} title={title}>
          {status}
        </RecordPage>
      );
    }
    return (
      <RecordModal onClose={onClose} kicker={<Kicker>Project</Kicker>} title={title} sub={sub}
        footer={<Action size="lg" variant="ghost" onClick={onClose}>Close</Action>}>
        {status}
      </RecordModal>
    );
  }

  const why = (
    <p className="m-0 text-[13.5px] leading-[22px] text-ink">
      {project.scope_notes}
      {!project.scope_notes && <span className="text-ink-soft">No scope note was recorded, so the reason this is a Project rather than a Quick Win has not been written down.</span>}
    </p>
  );
  const facts = (
    <FactList
      facts={[
        { label: 'Owner', value: project.owner || 'Not yet named' },
        { label: 'Audience', value: audienceValue(data.audience) },
        { label: 'Current state', value: project.state_label },
        { label: 'Next', value: project.next_action },
        { label: 'Department', value: project.department || 'Whole company' },
        { label: 'Raised', value: relativeTime(project.created_at) },
      ]}
    />
  );
  const work = (
    <>
      <HeadDecisionCard panel={data} onDecided={reload} onClose={afterDecision} />
      <OwnerDecisionCard project={project} onDecided={reload} onClose={afterDecision} />
      <OwnerQuoteCard requestId={project.id} quote={data.quote} quotes={data.quotes} onChanged={reload} />
      <RequestRoster
        requestId={project.id}
        status={project.status}
        scopers={project.scopers}
        builders={project.builders}
        canStaffScoping={canApprove || canDeptRollup}
        canStaffBuilding={canApprove}
        onChanged={reload}
      />
      <DeliveryCard panel={data} onChanged={reload} />
      <DocumentsSection requestId={project.id} />
    </>
  );
  const linked = (
    <div className="flex flex-col gap-1.5">
      {project.problem_id && (
        <Action variant="ghost" size="sm" to={`/projects?tab=opportunities&open=${project.problem_id}`} className="-ml-2.5 self-start">
          <RouteMark glyph="O" size="sm" /> Originating opportunity
        </Action>
      )}
      {!project.problem_id && (
        <div className="flex items-center gap-2.5 text-[13px] text-ink-dim"><RouteMark glyph="O" size="sm" /> Raised as a request, not from an Opportunity</div>
      )}
      <div className="flex items-center gap-2.5 text-[13px] text-ink-dim">
        <RouteMark kind="adoption" size="sm" /> Adoption readiness for the audience opens under AI Adoption
      </div>
    </div>
  );
  const controls = (
    <div className="space-y-2">
      {data.controls.map((c) => <ControlRow key={c.label} control={c} />)}
    </div>
  );
  const updates = visibleNotes.length > 0 && (
    <div className="divide-y divide-line-soft">
      {visibleNotes.map((n) => (
        <div key={n.id} className="py-2.5">
          <div className="text-[13px] text-ink">{n.body}</div>
          <div className="mt-0.5 text-[12px] text-ink-soft">{n.author_label} · {relativeTime(n.created_at)}</div>
        </div>
      ))}
    </div>
  );

  if (page) {
    return (
      <RecordPage
        backLabel={page.backLabel}
        onBack={onClose}
        kicker={<Kicker>Project</Kicker>}
        badge={chipBadge}
        title={title}
        sub={sub}
        actions={askOrbitAction({ label: 'Ask Orbit about this', size: 'lg', variant: 'quiet' })}
        aside={(
          <>
            <RailCard title="At a glance"><div className="[&>div>div]:px-5">{facts}</div></RailCard>
            <WorkspaceCard project={project} />
            <RailCard title="Linked"><div className="px-5 pb-4 pt-2">{linked}</div></RailCard>
            <RailCard title="Controls"><div className="px-4 pb-4 pt-2">{controls}</div></RailCard>
          </>
        )}
      >
        <Card pad="md">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Why this became a Project</div>
          {why}
        </Card>
        {work}
        {updates && (
          <Card pad="md">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Updates from delivery</div>
            {updates}
          </Card>
        )}
      </RecordPage>
    );
  }

  return (
    <RecordModal
      onClose={onClose}
      kicker={<Kicker>Project</Kicker>}
      badge={chipBadge}
      title={title}
      sub={sub}
      footer={(
        <div className="flex flex-wrap items-center gap-3">
          {askOrbitAction({ label: 'Ask Orbit about this', size: 'lg', variant: 'quiet' })}
          <Action size="lg" variant="ghost" onClick={onClose}>Close</Action>
        </div>
      )}
    >
      <div className="space-y-5">
        <div>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Why this became a Project</div>
          {why}
        </div>
        {facts}
        {work}
        <div>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Linked</div>
          {linked}
        </div>
        <div>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Controls</div>
          {controls}
        </div>
        <WorkspaceCard project={project} />
        {updates && (
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Updates from delivery</div>
            {updates}
          </div>
        )}
      </div>
    </RecordModal>
  );
};

/* /projects/:requestId — the Project record as its own page. */
const ProjectRecordPage: React.FC = () => {
  const { requestId = '' } = useParams<{ requestId: string }>();
  const { back, label } = useRecordBack();
  return <ProjectRecordDrawer requestId={requestId} onClose={back} page={{ backLabel: label }} />;
};

export default ProjectRecordPage;
